import { motion } from 'framer-motion';

import { useData } from 'vike-react/useData';

import { ArrowRight, ShieldAlert } from 'lucide-react';

import { getAudienceById } from '@shared/constants/audiences';

import { Container } from '../../components/layout/Container';

import { Reveal } from '../../components/motion/Reveal';

import { Button } from '../../components/ui/button';

import { CountUp } from '../../components/motion/CountUp';

import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';

import { JourneyTimeline } from '../../components/blocks/JourneyTimeline';

import { ToolsForAudience } from '../../components/blocks/ToolsForAudience';

import { ConcreteCase } from '../../components/blocks/ConcreteCase';

import { SectorHero } from '../../components/sector/SectorHero';

import { SectorProof } from '../../components/sector/SectorProof';

import { RecommendedCeeFiches } from '../../components/sector/RecommendedCeeFiches';

import { SectorFAQ } from '../../components/sector/SectorFAQ';

import { SectorCTA } from '../../components/sector/SectorCTA';

import { getAudienceVoice } from '../../config/audience-voice';

import { audienceLegacyTone } from '../../config/audience-theme';

import { easeStripe } from '../../lib/motion';

import type { Data } from './+data';

const AUDIENCE_ID = 'tertiaire' as const;

export default function TertiairePage() {
  const { journey, tools, cases, ceeFiches } = useData<Data>();

  const audience = getAudienceById(AUDIENCE_ID)!;

  const voice = getAudienceVoice(AUDIENCE_ID);

  const legacyTone = audienceLegacyTone(AUDIENCE_ID);

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },

          { name: 'Tertiaire', url: 'https://solutionecofrance.fr/tertiaire' },
        ]}
      />

      <SectorHero audience={audience} voice={voice} sideSlot={<OperatPreview />} />

      <SectorProof audience={audience} voice={voice} variant="bento" />

      <div id="methode" />

      {journey && (
        <JourneyTimeline
          eyebrow={voice.method.eyebrow}
          title={voice.method.title}
          description={voice.method.description}
          totalDuration={journey.totalDuration}
          ctaLabel={journey.ctaLabel}
          ctaHref={journey.ctaHref}
          steps={journey.steps}
          tone={legacyTone}
        />
      )}

      {tools.length > 0 && (
        <ToolsForAudience
          eyebrow={voice.tools.eyebrow}
          title={voice.tools.title}
          description={voice.tools.description}
          tools={tools.map((t) => ({
            title: t.title,

            description: t.description,

            iconName: t.iconName,

            href: t.href,

            badge: t.badge,

            metric: t.metric,

            metricLabel: t.metricLabel,
          }))}
          tone={legacyTone}
        />
      )}

      <RecommendedCeeFiches audience={audience} fiches={ceeFiches} />

      {cases.length > 0 && (
        <ConcreteCase
          eyebrow={voice.caseStudy.eyebrow}
          title={voice.caseStudy.title}
          cases={cases}
          tone={legacyTone}
        />
      )}

      {/* ── OPERAT 30 SEPT ALERT — spécifique tertiaire ── */}

      <section className="bg-stone-50/50 py-16 md:py-20">
        <Container className="max-w-5xl">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-stone-200/60 bg-gradient-to-br from-stone-100 via-paper to-ivory-100 p-8 md:p-10">
              <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
                <div className="max-w-2xl">
                  <div className="inline-flex items-center gap-1.5 rounded-md bg-stone-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink">
                    <ShieldAlert className="h-3 w-3" />
                    Déclaration OPERAT (plateforme Décret Tertiaire) — 30 septembre
                  </div>

                  <h3 className="h-section mt-4">
                    Votre déclaration sur la plateforme OPERAT (Décret Tertiaire) est-elle prête
                    pour le 30 septembre&nbsp;?
                  </h3>

                  <p className="mt-3 text-sm leading-relaxed text-stone-600">
                    Le décret n° 2019-771 impose à tout patrimoine tertiaire ≥ 1 000 m² une
                    déclaration annuelle des consommations sur la plateforme OPERAT et une
                    trajectoire de réduction (-40 % en 2030, -50 % en 2040, -60 % en 2050) versus
                    une année de référence comprise entre 2010 et 2019. En cas de non-respect&nbsp;:
                    mise en demeure publiée, puis amende administrative.
                  </p>

                  <Button asChild size="md" variant="primary" className="group/btn mt-6">
                    <a href="/tertiaire/decret-tertiaire">
                      Lancer l&rsquo;audit Décret Tertiaire (OPERAT)
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      <SectorFAQ audience={audience} voice={voice} />

      <SectorCTA audience={audience} voice={voice} id="audit-operat" />
    </>
  );
}

// ════════════════════════════════════════════════════════════════

// OperatPreview — side card hero

// Aperçu d'un audit OPERAT en cours (immeuble tertiaire, trajectoire

// -40 %, plan d'actions financé par CEE BAT).

// ════════════════════════════════════════════════════════════════

function OperatPreview() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.18, ease: easeStripe }}
      className="rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] ring-1 ring-inset ring-stone-200/60 md:p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            Audit Décret Tertiaire (OPERAT) en cours
          </p>

          <p className="mt-0.5 text-xs text-stone-500">Immeuble tertiaire · 8 200 m²</p>
        </div>

        <span className="inline-flex items-center gap-1 rounded-md bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-ink ring-1 ring-inset ring-stone-200/50">
          <span className="h-1 w-1 animate-glow-pulse rounded-full bg-stone-600" />
          Trajectoire
        </span>
      </div>

      <div className="mt-5 flex items-baseline gap-2 font-display tracking-tight">
        <span className="text-3xl font-bold text-ink md:text-4xl">
          −<CountUp end={40} />
          &nbsp;%
        </span>

        <span className="text-base text-stone-500">objectif 2030</span>
      </div>

      <div className="mt-5 space-y-1.5">
        {[
          {
            label: 'Pompe à chaleur tertiaire',

            sublabel: 'fiche CEE pompe à chaleur tertiaire (BAT-TH-104)',

            val: 'Phase 1',

            status: 'Engagé',
          },

          {
            label: 'Pilotage intelligent du bâtiment (GTB)',

            sublabel: 'fiche CEE pilotage du bâtiment (BAT-TH-116)',

            val: 'Phase 2',

            status: 'Étude',
          },

          {
            label: 'Isolation de toiture-terrasse',

            sublabel: 'fiche CEE isolation toiture-terrasse (BAT-EN-101)',

            val: 'Phase 3',

            status: 'Planifié',
          },
        ].map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between rounded-lg border border-stone-200/70 bg-stone-50/40 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-stone-700">{row.label}</p>

              <p className="mt-0.5 text-[10px] text-stone-500">{row.sublabel}</p>
            </div>

            <span className="ml-2 font-mono text-xs font-semibold tabular-nums text-ink">
              {row.val}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-stone-200/60 pt-3">
        <p className="text-[11px] text-stone-500">Plan financé par les primes énergie</p>

        <p className="font-mono text-[12px] font-semibold tabular-nums text-stone-700">
          Primes énergie — Primes énergie — CEE (312 k€ estimés)
        </p>
      </div>
    </motion.div>
  );
}
